import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { api } from "../../api";
import type { Announcement, PublishedPage } from "../../../shared/types";
import { Badge, Dialog, ErrorNote } from "../../ui";
import { useSession } from "../../shell/session";
import { PageFrame, hasRole, messageOf, slugify } from "../calendar/chrome";
import { formatDateKey } from "../calendar/dates";
import { Article } from "./Article";

const PAGE_SLUG = /^[a-z0-9-]{2,60}$/;

export function PublishingPage() {
  const { user } = useSession();
  const canEdit = hasRole(user?.role, "leader");
  const [pages, setPages] = useState<PublishedPage[]>([]);
  const [notes, setNotes] = useState<Announcement[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newSlug, setNewSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newBody, setNewBody] = useState("");
  const [publishNow, setPublishNow] = useState(false);
  const [annTitle, setAnnTitle] = useState("");
  const [annBody, setAnnBody] = useState("");
  const [annStatus, setAnnStatus] = useState<"draft" | "published">("draft");
  const [annDate, setAnnDate] = useState("");
  const [publicCopy, setPublicCopy] = useState<PublishedPage | null>(null);
  const [publicError, setPublicError] = useState("");
  const [liveNotes, setLiveNotes] = useState<Announcement[] | null>(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    if (!canEdit) return;
    let live = true;
    void Promise.all([
      api<PublishedPage[]>("/api/v1/publishing/pages"),
      api<Announcement[]>("/api/v1/publishing/announcements"),
    ]).then(([nextPages, nextNotes]) => {
      if (!live) return;
      setPages(nextPages);
      setNotes(nextNotes);
      setSelectedId((current) => current || nextPages[0]?.id || "");
    }).catch((caught: unknown) => { if (live) setError(messageOf(caught)); });
    return () => { live = false; };
  }, [canEdit, reload]);

  const selected = pages.find((page) => page.id === selectedId) ?? null;
  useEffect(() => {
    setTitle(selected?.title ?? "");
    setBody(selected?.body ?? "");
    setPublicCopy(null);
    setPublicError("");
  }, [selected?.id, selected?.updatedAt]);

  const dirty = selected != null && (title !== selected.title || body !== selected.body);

  async function save(status: "draft" | "published") {
    if (!selected) return;
    if (!title.trim()) {
      setError("Give the page a title.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const saved = await api<PublishedPage>(`/api/v1/publishing/pages/${selected.id}`, {
        method: "PATCH",
        body: JSON.stringify({ title: title.trim(), body, status }),
      });
      setPages((current) => current.map((page) => page.id === saved.id ? saved : page));
      setNotice(status === "published" ? "Published. The public address now returns this copy." : "Saved as a draft. The public address will not show it.");
    } catch (caught) {
      setError(messageOf(caught));
    } finally {
      setSaving(false);
    }
  }

  async function createPage(event: FormEvent) {
    event.preventDefault();
    setError("");
    if (!newTitle.trim()) {
      setError("Give the page a title.");
      return;
    }
    if (!PAGE_SLUG.test(newSlug)) {
      setError("The address needs 2–60 characters: lowercase letters, numbers, and hyphens.");
      return;
    }
    setSaving(true);
    try {
      const created = await api<PublishedPage>("/api/v1/publishing/pages", {
        method: "POST",
        body: JSON.stringify({ slug: newSlug, title: newTitle.trim(), body: newBody, status: publishNow ? "published" : "draft" }),
      });
      setCreating(false);
      setNewSlug("");
      setNewTitle("");
      setNewBody("");
      setPublishNow(false);
      setSlugTouched(false);
      setSelectedId(created.id);
      setReload((value) => value + 1);
      setNotice(created.status === "published" ? "Page created and published." : "Draft page created.");
    } catch (caught) {
      setError(messageOf(caught));
    } finally {
      setSaving(false);
    }
  }

  async function createAnnouncement(event: FormEvent) {
    event.preventDefault();
    setError("");
    if (!annTitle.trim() || !annBody.trim()) {
      setError("An announcement needs a title and some text.");
      return;
    }
    if (annDate && !/^\d{4}-\d{2}-\d{2}$/.test(annDate)) {
      setError("Publish date must be a calendar date.");
      return;
    }
    setSaving(true);
    try {
      await api<Announcement>("/api/v1/publishing/announcements", {
        method: "POST",
        body: JSON.stringify({ title: annTitle.trim(), body: annBody.trim(), status: annStatus, publishOn: annDate || null }),
      });
      setAnnTitle("");
      setAnnBody("");
      setAnnDate("");
      setAnnStatus("draft");
      setReload((value) => value + 1);
      setNotice(annStatus === "published" ? "Announcement published to the portal." : "Announcement saved as a draft. It is not on the portal, and it cannot be published later.");
    } catch (caught) {
      setError(messageOf(caught));
    } finally {
      setSaving(false);
    }
  }

  async function loadPublic() {
    if (!selected) return;
    setPublicError("");
    setPublicCopy(null);
    try {
      setPublicCopy(await api<PublishedPage>(`/api/v1/public/pages/${encodeURIComponent(selected.slug)}`));
    } catch (caught) {
      setPublicError(messageOf(caught));
    }
  }

  async function loadLiveNotes() {
    setError("");
    try {
      setLiveNotes(await api<Announcement[]>("/api/v1/public/announcements"));
    } catch (caught) {
      setError(messageOf(caught));
    }
  }

  if (!canEdit) {
    return (
      <PageFrame kicker="Operations" title="Publishing" lede="Pages and announcements for the member portal.">
        <p className="notice">Publishing needs a leader login. Members can read what is already published on the portal.</p>
        <Link className="btn" to="/portal">Open the portal</Link>
      </PageFrame>
    );
  }

  return (
    <PageFrame
      kicker="Operations"
      title="Publishing"
      lede="Edit local pages, preview the draft, then publish. The public copy is a separate read of what visitors would get."
      actions={<button className="btn primary" type="button" onClick={() => setCreating(true)}>New page</button>}
    >
      {error && <ErrorNote>{error}</ErrorNote>}
      {notice && <p className="ok-note" role="status">{notice}</p>}
      <div className="pub-grid">
        <div className="stack">
          <article className="panel panel-pad stack">
            <h2>Pages</h2>
            {pages.map((page) => (
              <button key={page.id} className="btn choice" type="button" aria-pressed={page.id === selectedId} onClick={() => setSelectedId(page.id)}>
                <span>{page.title}</span>
                <Badge tone={page.status === "published" ? "teal" : "amber"}>{page.status}</Badge>
              </button>
            ))}
          </article>
          {selected && (
            <form className="panel panel-pad form-grid" onSubmit={(event) => { event.preventDefault(); void save(selected.status); }}>
              <p className="faint">Address /{selected.slug} is fixed. The API cannot rename it.</p>
              <label className="field">Title<input value={title} onChange={(event) => setTitle(event.target.value)} maxLength={160} /></label>
              <label className="field">Body<textarea value={body} onChange={(event) => setBody(event.target.value)} maxLength={20000} /></label>
              <p className="faint">{dirty ? "Unsaved edits are only in this preview until you save." : "The editor matches the saved page."} Plain text only — markup is shown as written, not rendered.</p>
              <div className="cluster">
                <button className="btn" type="submit" disabled={saving}>{saving ? "Saving…" : "Save"}</button>
                <button className="btn primary" type="button" disabled={saving} onClick={() => void save("published")}>Publish</button>
                <button className="btn ghost" type="button" disabled={saving || selected.status === "draft"} onClick={() => void save("draft")}>Unpublish</button>
              </div>
            </form>
          )}
          <form className="panel panel-pad form-grid" onSubmit={(event) => void createAnnouncement(event)}>
            <h2>Announcement</h2>
            <p className="faint">Choose published now if it should appear on the portal. Announcements cannot be edited or published later — there is no update route.</p>
            <label className="field">Title<input value={annTitle} onChange={(event) => setAnnTitle(event.target.value)} maxLength={160} /></label>
            <label className="field">Body<textarea value={annBody} onChange={(event) => setAnnBody(event.target.value)} maxLength={8000} /></label>
            <label className="field">Status
              <select value={annStatus} onChange={(event) => setAnnStatus(event.target.value as "draft" | "published")}>
                <option value="draft">Draft — hidden from the portal</option>
                <option value="published">Published — visible on the portal</option>
              </select>
            </label>
            <label className="field">Publish date<input type="date" value={annDate} onChange={(event) => setAnnDate(event.target.value)} /></label>
            <button className="btn primary" type="submit" disabled={saving}>{saving ? "Saving…" : "Post announcement"}</button>
          </form>
        </div>
        <div className="stack">
          <div>
            <Article kicker={dirty ? "Unsaved preview" : "Saved draft preview"} title={title} body={body} />
          </div>
          <article className="panel panel-pad stack">
            <h2>Public copy</h2>
            <p className="muted">This reads `/api/v1/public/pages/{selected?.slug ?? ""}`. Drafts return not found. There is no separate public route mounted for pages yet; the portal shows published pages inside the app.</p>
            <button className="btn" type="button" disabled={!selected} onClick={() => void loadPublic()}>Load public copy</button>
            {publicError && <p className="notice">{publicError}</p>}
            {publicCopy && <Article kicker="Returned by the public API" title={publicCopy.title} body={publicCopy.body} />}
            <Link className="btn ghost" to="/portal">Open the member portal</Link>
          </article>
          <article className="panel panel-pad stack">
            <div className="split"><h2>Announcements</h2><button className="btn small" type="button" onClick={() => void loadLiveNotes()}>Load public list</button></div>
            {notes.map((note) => (
              <div key={note.id}>
                <div className="split"><strong>{note.title}</strong><Badge tone={note.status === "published" ? "teal" : "amber"}>{note.status}</Badge></div>
                <p className="prose muted">{note.body}</p>
                <p className="faint">{note.publishOn ? formatDateKey(note.publishOn) : "No publish date"}</p>
              </div>
            ))}
            {liveNotes && (
              <div>
                <h3>Public list</h3>
                {liveNotes.length === 0 ? <p className="muted">No published announcements.</p> : liveNotes.map((note) => <p key={note.id}>{note.title}</p>)}
              </div>
            )}
          </article>
        </div>
      </div>
      {creating && (
        <Dialog title="New page" onClose={() => setCreating(false)}>
          <form className="form-grid" onSubmit={(event) => void createPage(event)}>
            <label className="field">Title<input value={newTitle} onChange={(event) => { setNewTitle(event.target.value); if (!slugTouched) setNewSlug(slugify(event.target.value)); }} maxLength={160} /></label>
            <label className="field">Address<input value={newSlug} onChange={(event) => { setSlugTouched(true); setNewSlug(event.target.value.trim().toLowerCase()); }} placeholder="plan-a-visit" /></label>
            <label className="field">Body<textarea value={newBody} onChange={(event) => setNewBody(event.target.value)} maxLength={20000} /></label>
            <label className="cluster"><input type="checkbox" checked={publishNow} onChange={(event) => setPublishNow(event.target.checked)} /> Publish now</label>
            <button className="btn primary" type="submit" disabled={saving}>{saving ? "Saving…" : "Create page"}</button>
          </form>
        </Dialog>
      )}
    </PageFrame>
  );
}
